<template>
  <div
    class="assessment-floating assessment-collapsed card"
    :class="{ 'assessment-collapsed': collapse, 'assessment-show': !collapse }"
  >
    <div class="card-header header-sticky">
      <div class="card-title float-left">Ocena</div>

      <span class="float-right">
        <button class="btn btn-secondary btn-xs mr-2" @click="cancel">Wróć</button>

        <button class="btn btn-primary btn-xs mr-2" @click="save">Zapisz i zostań</button>

        <button class="btn btn-primary btn-xs mr-2" @click="saveAndExit">Zapisz i wróć</button>
      </span>
    </div>

    <div class="card-body">
      <div v-for="(model, index) in $schema.data.assessments.$items" :key="`assessment_value_${index}`">
        <assessment-value :criterion="criterions[index]" :model="model.$item" />
      </div>
    </div>

    <div class="footer-sticky text-center">
      <button class="btn btn-block btn-xs" @click="toggleCollapse">
        <tooltip v-if="collapse" text="Rozwiń">
          <i class="fal fa-angle-double-down" />
        </tooltip>

        <tooltip v-else text="Zwiń">
          <i class="fal fa-angle-double-up" />
        </tooltip>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Tooltip from '@/components/tooltip/Tooltip';
import { Errors } from '@/enums/errors.enum';
import { Successes } from '@/enums/successes.enum';
import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import { assessmentResource } from '@/resources/assessment.resource';
import schema from '@/schemas/assessment.schema';
import { sortCriterions, sortedCriterion } from '@/utils/criterion';
import { getProfileId } from '@/utils/profile';

import AssessmentValue from './AssessmentValue';

// @vuese
// Ocena
export default {
  components: { AssessmentValue, Tooltip },
  mixins: [formMixin, notificationsMixin],
  schema,
  data() {
    return {
      model: {},
      collapse: false
    };
  },
  computed: mapState({
    criterions: state => sortCriterions(state.criterion.list),
    assessment: state => (state.assessment.list.length ? state.assessment.list[0] : undefined)
  }),
  mounted() {
    this.$schema.$setData(this.getData());
    this.criterions.forEach(criterion => this.$schema.data.assessments.$addRow(this.rowData(criterion.id)));
  },
  beforeCreate() {
    this.$options.external = { criterions: sortCriterions(this.$store.state.criterion.list) };
  },
  methods: {
    // @vuese
    // Przechodzi do widoku szczegółów dokumentów
    goToDocumentView() {
      this.$router.push({ name: 'documentView', query: this.$route.query });
    },
    // @vuese
    // Otwiera okienko z potwierdzeniem opuszczenia formularza
    // @arg `andSave` -  czy zapisać formularz?
    // @arg `description` - tekst potwierdzenia
    showExitDialog(andSave, description) {
      this.$store.dispatch('setConfirm', {
        title: 'Błędy',
        description,
        closeButton: 'Nie - kontynuuj edycję formularza',
        submitButton: 'Tak - poprawię formularz później',
        onSuccess: () => {
          if (andSave) {
            return this.sendSaveRequest()
              .then(() => {
                this.notifySuccess(Successes.ASSESSMENT_SAVED);
                this.goToDocumentView();
              })
              .catch(() => this.notifyError(Errors.ASSESSMENT_CANNOT_SAVE));
          }
        }
      });
    },
    // @vuese
    // Anuluje dodawanie/edycję
    cancel() {
      if (this.$schema.$validateAll().length) {
        return this.showExitDialog(false, 'Formularz zawiera błędy. Czy chcesz zostawić go w obecnej formie?');
      }

      this.goToDocumentView();
    },
    // @vuese
    // Zapisuje ocenę i wraca do szczegółów dokumentów
    saveAndExit() {
      if (this.$schema.$validateAll().length) {
        return this.showExitDialog(true, 'Formularz zawiera błędy. Czy chcesz zapisać go w obecnej formie?');
      }

      this.save(() => this.goToDocumentView());
    },
    // @vuese
    // Zapisuje ocenę
    save(onSuccess) {
      this.sendSaveRequest()
        .then(res => {
          this.notifySuccess(Successes.ASSESSMENT_SAVED);
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(res);
          }
        })
        .catch(err => this.notifyError(Errors.ASSESSMENT_CANNOT_SAVE));
    },
    // @vuese
    // Wysyła request z zapisem oceny
    sendSaveRequest() {
      if (this.assessment) {
        return assessmentResource.update(this.assessment.id, this.$schema.$getData());
      }
      return assessmentResource.add(this.$schema.$getData());
    },
    // @vuese
    // Znajduje dane odpowiedniego kryterium
    rowData(criterionId) {
      const defaultData = { criterionId };
      if (this.assessment) {
        return this.assessment.data.assessments.find(value => value.criterionId === criterionId) || defaultData;
      }
      return defaultData;
    },
    // @vuese
    // Zwraca dane oceny
    getData() {
      if (!this.assessment) {
        return {
          name: getProfileId().toString(),
          documentId: parseInt(this.$route.params.documentId),
          profileId: getProfileId(),
          data: { assessments: [] }
        };
      }
      return this.getStoreDataWithoutValues();
    },
    // @vuese
    // Zwraca dane oceny ze store, bez wartości ocen
    getStoreDataWithoutValues() {
      const data = { ...this.assessment, profileId: getProfileId() };
      data.data = { ...data.data, assessments: [] };
      return data;
    },
    // @vuese
    // Rozwija panel oceny
    toggleCollapse() {
      this.collapse = !this.collapse;
    }
  }
};
</script>

<style>
.assessment-floating {
  position: sticky;
  top: 0;
  z-index: 1000;
  overflow: auto;
}

.assessment-collapsed {
  height: 30vh;
}

.assessment-show {
  height: 75vh;
}

.assessment-floating .card-header {
  background-color: #fff;
}
.header-sticky {
  position: sticky;
  z-index: 1001;
  top: 0;
}
.footer-sticky {
  position: sticky;
  z-index: 1001;
  bottom: 0;
  background-color: white;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
